import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Building2, ExternalLink, Loader2, FileDown } from "lucide-react";
import api, { API } from "@/lib/api";
import Layout from "@/components/Layout";
import { TypeTag, YearTag } from "@/components/Tag";

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch (_e) {
    return iso;
  }
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    api
      .get(`/public/opportunities/${id}`)
      .then(({ data }) => { if (!cancel) setOpp(data); })
      .catch((e) => { if (!cancel) setError(e?.response?.status === 404 ? "This opportunity is no longer available." : "Failed to load."); });
    return () => { cancel = true; };
  }, [id]);

  return (
    <Layout variant="public">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold mb-6 hover:underline" data-testid="back-to-board">
          <ArrowLeft className="h-4 w-4" />
          Back to board
        </Link>

        {error ? (
          <div className="brut-card-static p-10 text-center" data-testid="detail-error">
            <div className="font-display text-2xl font-black mb-2">Oops</div>
            <p className="text-zinc-700">{error}</p>
          </div>
        ) : !opp ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C8102E]" /></div>
        ) : (
          <article className="brut-card-static p-8 sm:p-10" data-testid="detail-card">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <TypeTag type={opp.type} />
              {opp.year_groups?.map((y) => <YearTag key={y} year={y} />)}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-4">{opp.title}</h1>

            <div className="grid sm:grid-cols-2 gap-3 mb-6 text-sm">
              {opp.event_date && (
                <div className="flex items-center gap-2 font-medium"><Calendar className="h-4 w-4" /> {formatDate(opp.event_date)}</div>
              )}
              {opp.location && (
                <div className="flex items-center gap-2 font-medium"><MapPin className="h-4 w-4" /> {opp.location}</div>
              )}
              {opp.provider && (
                <div className="flex items-center gap-2 font-medium"><Building2 className="h-4 w-4" /> {opp.provider}</div>
              )}
            </div>

            <div className="prose max-w-none mb-6 whitespace-pre-wrap text-zinc-800 font-medium leading-relaxed" data-testid="detail-description">
              {opp.description}
            </div>

            {opp.links && opp.links.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="detail-links">
                {opp.links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className="brut-btn-primary inline-flex"
                    data-testid={`detail-link-${i}`}
                  >
                    {l.label || "Visit link"}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}

            {opp.files && opp.files.length > 0 && (
              <div className="border-t-2 border-black pt-6 mt-6">
                <h3 className="font-display font-bold text-lg mb-3">Attachments</h3>
                <ul className="space-y-2">
                  {opp.files.map((f) => (
                    <li key={f.id}>
                      <a
                        href={`${API}/files/${f.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 brut-card-static px-4 py-2 text-sm font-bold hover:bg-zinc-50"
                        data-testid={`detail-file-${f.id}`}
                      >
                        <FileDown className="h-4 w-4" />
                        {f.original_filename}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>
        )}
      </div>
    </Layout>
  );
}
